import React from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, MapIcon, User, PlusCircle, Shield, LogOut, Menu, X, Heart } from 'lucide-react';
import { Button } from './ui/button';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-[#0A0A0A] border-b border-[#0A0A0A] shadow-sm" data-testid="main-header">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src="/logos/v1-black-bg.png"
              alt="Mi Tribu"
              className="w-10 h-10 rounded-lg ring-1 ring-white/20"
            />
            <span className="text-xl font-heading font-bold text-white hidden sm:block">Mi Tribu</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                isActive('/') && !isActive('/explorar') ? 'text-primary' : 'text-white/70 hover:text-white'
              }`}
              data-testid="nav-home"
            >
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <Link
              to="/explorar"
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                isActive('/explorar') ? 'text-primary' : 'text-white/70 hover:text-white'
              }`}
              data-testid="nav-explore"
            >
              <MapIcon className="w-4 h-4" />
              Explorar
            </Link>
            {user && (
              <Link
                to="/favoritos"
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  isActive('/favoritos') ? 'text-primary' : 'text-white/70 hover:text-white'
                }`}
                data-testid="nav-favorites"
              >
                <Heart className="w-4 h-4" />
                Favoritos
                {user.favorites?.length > 0 && (
                  <span className="ml-1 text-[10px] font-bold bg-primary text-white rounded-full px-1.5 py-0.5">
                    {user.favorites.length}
                  </span>
                )}
              </Link>
            )}
            {user?.role === 'organizer' || user?.role === 'admin' ? (
              <Link
                to="/organizador"
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  isActive('/organizador') ? 'text-primary' : 'text-white/70 hover:text-white'
                }`}
                data-testid="nav-organizer"
              >
                <PlusCircle className="w-4 h-4" />
                Organizador
              </Link>
            ) : null}
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  isActive('/admin') ? 'text-primary' : 'text-white/70 hover:text-white'
                }`}
                data-testid="nav-admin"
              >
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/perfil">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden md:flex items-center gap-2 text-white hover:text-primary"
                    data-testid="profile-button"
                  >
                    <User className="w-4 h-4" />
                    {user.name}
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="hidden md:flex items-center gap-2 text-white/70 hover:text-white"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="hidden md:block text-white/70 hover:text-white" data-testid="login-link">
                    Iniciar sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="hidden md:block bg-primary hover:bg-primary/90 text-white" data-testid="register-link">
                    Registrarse
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white hover:text-primary"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-[#0A0A0A]" data-testid="mobile-menu">
            <nav className="container mx-auto px-4 py-4 space-y-2">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 p-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
              >
                <Home className="w-5 h-5" />
                Inicio
              </Link>
              <Link
                to="/explorar"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 p-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
              >
                <MapIcon className="w-5 h-5" />
                Explorar
              </Link>
              {user ? (
                <>
                  <Link
                    to="/favoritos"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
                    data-testid="nav-favorites-mobile"
                  >
                    <Heart className="w-5 h-5" />
                    Favoritos
                    {user.favorites?.length > 0 && (
                      <span className="ml-1 text-xs font-bold bg-primary text-white rounded-full px-2 py-0.5">
                        {user.favorites.length}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/perfil"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    <User className="w-5 h-5" />
                    Perfil
                  </Link>
                  {(user.role === 'organizer' || user.role === 'admin') && (
                    <Link
                      to="/organizador"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
                    >
                      <PlusCircle className="w-5 h-5" />
                      Organizador
                    </Link>
                  )}
                  {user.role === 'admin' && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
                    >
                      <Shield className="w-5 h-5" />
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 p-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    Cerrar sesión
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block p-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    Iniciar sesión
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block p-3 rounded-lg bg-primary text-white text-center"
                  >
                    Registrarse
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E5E5] mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-sm text-[#737373]">
            <p>© 2026 Mi Tribu. Conectando comunidades en Salta y el NOA.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
